import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronRight, House, Inbox, KeyRound } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { can, useMe } from '../auth.tsx';
import { Modal } from '../components/Modal.tsx';
import { Button } from '../components/ui.tsx';
import { visibleAreas } from '../navigation.ts';
import { useUnreadCount } from '../notifications.tsx';
import {
  type DailyItem,
  greetingKey,
  homeDaily,
  homeServices,
  type MyRequests,
  myRequests,
} from './home-shortcuts.ts';
import {
  Block,
  LinksBlock,
  MainBlocks,
  SideBlocks,
  useIntranetHome,
} from './intranet/HomeBlocks.tsx';

/**
 * Início (ADR 0071 e 0077, revisão de 29/10/2026): a intranet da LOOT e a porta de entrada para os
 * serviços. Saudação compacta; a faixa "O que você precisa?" com os formulários de abertura que a
 * pessoa pode usar; comunicados e novidades na coluna principal e aniversariantes e agenda na de
 * apoio; embaixo, Meu dia a dia e links úteis. Cada bloco fica sempre no lugar e diz se está
 * carregando, vazio ou com falha: nunca some, nunca mostra conteúdo de exemplo.
 */
export function HomePage() {
  const { t, i18n } = useTranslation();
  const { data: me } = useMe();
  const areas = useMemo(() => visibleAreas((p) => can(me, p)), [me]);
  const services = useMemo(() => homeServices(areas), [areas]);
  const requests = useMemo(() => myRequests(areas), [areas]);
  const daily = useMemo(() => homeDaily(areas), [areas]);
  const home = useIntranetHome();
  const [mineOpen, setMineOpen] = useState(false);
  if (!me) return null;
  const now = new Date();
  const today = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(now);
  return (
    <div className="mx-auto flex w-full max-w-[1180px] flex-col gap-5">
      {/* Saudação compacta: o espaço do alto é dos serviços e do conteúdo. */}
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <h1 className="text-xl font-semibold tracking-tight">
          {t(greetingKey(now), { name: me.user.displayName })}
        </h1>
        <p className="text-sm text-muted">
          {today.charAt(0).toLocaleUpperCase(i18n.language) + today.slice(1)}
        </p>
      </div>

      {services.length > 0 && (
        <section aria-labelledby="home-services" className="flex flex-col gap-2.5">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <h2 id="home-services" className="text-base font-semibold">
              {t('home.services.title')}
            </h2>
            <span className="text-xs text-muted">{t('home.services.caption')}</span>
            {requests.length > 0 && (
              <button
                type="button"
                onClick={() => setMineOpen(true)}
                className="ml-auto inline-flex min-h-9 items-center gap-1.5 text-sm font-medium text-brand-text hover:underline"
              >
                <Inbox size={16} strokeWidth={1.75} aria-hidden />
                {t('home.services.mine')}
              </button>
            )}
          </div>
          <ul className="grid grid-cols-2 gap-3 sm:grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
            {services.map((s) => {
              const Icon = s.icon;
              return (
                <li key={s.id}>
                  <Link
                    to={s.to}
                    title={t(`home.services.${s.id}.caption`)}
                    className="flex h-full items-center gap-3 rounded-lg border border-rule bg-sheet px-3 py-3 text-ink shadow-sm hover:border-control-border sm:items-start"
                  >
                    <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-paper text-brand-text">
                      <Icon size={18} strokeWidth={1.75} aria-hidden />
                    </span>
                    <span className="flex min-w-0 flex-col">
                      <span className="text-sm font-semibold">
                        {t(`home.services.${s.id}.title`)}
                      </span>
                      <span className="hidden text-xs text-muted sm:block">
                        {t(`home.services.${s.id}.caption`)}
                      </span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {/*
        Duas colunas no computador (2 : 1). No celular, uma coluna só, na ordem: comunicados,
        aniversariantes, novidades, agenda (as colunas viram "contents" e cada bloco tem a ordem).
      */}
      <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)] lg:items-start">
        <div className="contents lg:flex lg:min-w-0 lg:flex-col lg:gap-5">
          <MainBlocks home={home} />
        </div>
        <div className="contents lg:flex lg:min-w-0 lg:flex-col lg:gap-5">
          <SideBlocks home={home} />
        </div>
      </div>

      <div className="grid items-start gap-5 lg:grid-cols-2">
        <DailyBlock items={daily} requests={requests} onMine={() => setMineOpen(true)} />
        <LinksBlock home={home} />
      </div>

      <MyRequestsModal open={mineOpen} requests={requests} onClose={() => setMineOpen(false)} />
    </div>
  );
}

/** Meu dia a dia: o acompanhamento pessoal, compacto, com as fontes das próprias telas. */
function DailyBlock({
  items,
  requests,
  onMine,
}: {
  items: DailyItem[];
  requests: MyRequests[];
  onMine: () => void;
}) {
  const { t, i18n } = useTranslation();
  const counts = useCounts(items);
  const row = 'flex min-h-12 items-center gap-3 py-2';
  const value = (count: Count | undefined) => {
    if (!count) return null;
    if (count.state === 'loading')
      return (
        <span
          role="status"
          aria-label={t('common.loading')}
          className="h-3 w-6 animate-pulse rounded bg-rule"
        />
      );
    if (count.state === 'error')
      return <span className="text-xs font-medium text-danger">{t('home.daily.failed')}</span>;
    return (
      <span className="text-sm font-semibold tabular-nums">
        {count.value.toLocaleString(i18n.language)}
      </span>
    );
  };
  return (
    <Block icon={House} title={t('home.daily.title')}>
      <ul className="flex flex-col divide-y divide-rule">
        {requests.length > 0 && (
          <li>
            <button type="button" onClick={onMine} className={`${row} w-full text-left`}>
              <Inbox size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
              <span className="flex min-w-0 grow flex-col">
                <span className="text-sm font-medium">{t('home.daily.requests')}</span>
                <span className="text-xs text-muted">
                  {requests.map((r) => t(r.areaKey)).join(', ')}
                </span>
              </span>
              <ChevronRight size={16} aria-hidden className="shrink-0 text-control-border" />
            </button>
          </li>
        )}
        {items.map((d) => {
          const Icon = d.icon;
          return (
            <li key={d.id}>
              <Link to={d.to} className={`${row} text-ink hover:text-brand-text`}>
                <Icon size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
                <span className="flex min-w-0 grow flex-col">
                  <span className="text-sm font-medium">{t(`home.daily.${d.id}`)}</span>
                  <span className="text-xs text-muted">{t(`home.daily.${d.id}Hint`)}</span>
                </span>
                {value(d.count && counts[d.count])}
                <ChevronRight size={16} aria-hidden className="shrink-0 text-control-border" />
              </Link>
            </li>
          );
        })}
      </ul>
      {/* Meus acessos: secundário e recolhido; a tela própria abre também pelo rodapé da lateral. */}
      <details className="border-t border-rule pt-3 text-sm">
        <summary className="cursor-pointer font-medium text-muted">
          {t('home.daily.myAccess')}
        </summary>
        <p className="mt-2 text-muted">{t('home.daily.myAccessHint')}</p>
        <Link
          to="/my-access"
          className="mt-1 inline-flex items-center gap-1.5 font-medium text-brand-text hover:underline"
        >
          <KeyRound size={14} aria-hidden />
          {t('home.daily.myAccessOpen')}
        </Link>
      </details>
    </Block>
  );
}

/**
 * Minhas solicitações: os caminhos para a tela de cada área. Sem soma nem lista juntada por fora dos
 * módulos (a lista de Facilities, por exemplo, traz também a fila de quem atende).
 */
function MyRequestsModal({
  open,
  requests,
  onClose,
}: {
  open: boolean;
  requests: MyRequests[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={open}
      onClose={onClose}
      size="sm"
      title={t('home.daily.requests')}
      description={t('home.services.mineDescription')}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <ul className="flex flex-col divide-y divide-rule">
        {requests.map((r) => {
          const Icon = r.icon;
          return (
            <li key={r.to}>
              <Link
                to={r.to}
                onClick={onClose}
                className="flex min-h-12 items-center gap-3 py-2 text-ink hover:text-brand-text"
              >
                <Icon size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted" />
                <span className="grow text-sm font-medium">{t(r.areaKey)}</span>
                <ChevronRight size={16} aria-hidden className="shrink-0 text-control-border" />
              </Link>
            </li>
          );
        })}
      </ul>
    </Modal>
  );
}

type CountId = NonNullable<DailyItem['count']>;

/** Uma contagem do Meu dia a dia: carregando, falha ou o número. Falha nunca vira zero. */
type Count = { state: 'loading' } | { state: 'error' } | { state: 'ok'; value: number };

function countOf<T>(q: UseQueryResult<T>, value: (data: T) => number): Count {
  if (q.isError) return { state: 'error' };
  if (q.isPending) return { state: 'loading' };
  return { state: 'ok', value: value(q.data) };
}

/** As contagens do Meu dia a dia, das mesmas consultas (e do mesmo cache) das telas de destino. */
function useCounts(items: DailyItem[]): Partial<Record<CountId, Count>> {
  const has = (id: CountId) => items.some((d) => d.count === id);
  const approvals = useQuery({
    queryKey: ['approvals', 'inbox'],
    queryFn: () => api.get<unknown[]>('/approvals/inbox'),
    enabled: has('approvals'),
  });
  const assets = useQuery({
    queryKey: ['assets', 'mine'],
    queryFn: () => api.get<{ assets: unknown[] }>('/assets/mine'),
    enabled: has('assets'),
  });
  const unread = useUnreadCount();
  return {
    ...(has('approvals') ? { approvals: countOf(approvals, (r) => r.length) } : {}),
    ...(has('assets') ? { assets: countOf(assets, (r) => r.assets.length) } : {}),
    notifications: countOf(unread, (r) => r.count),
  };
}
